import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { t, formatEur } from "@/i18n/sk";
import { useCart } from "@/context/CartContext";
import { getPaymentStatus } from "@/lib/api";

const MAX_TRIES = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let tries = 0, active = true;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (!active) return;
        if (res.payment_status === "paid") {
          setOrder(res);
          setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (res.status === "expired" || res.payment_status === "failed") { setState("failed"); return; }
      } catch (e) { /* keep polling */ }
      tries += 1;
      if (tries >= MAX_TRIES) { setState("failed"); return; }
      if (active) setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, clear]);

  return (
    <div className="vault-bg">
      <section className="mx-auto flex max-w-xl flex-col items-center gap-5 px-4 py-24 text-center">
        {state === "checking" && (
          <div data-testid="payment-checking" className="flex flex-col items-center gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-violet-glow" />
            <p className="text-slate-300">{t.payment.checking}</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-success" className="flex flex-col items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15">
              <CheckCircle2 className="h-9 w-9 text-emerald-400" />
            </div>
            <h1 className="font-display text-3xl font-extrabold text-white">{t.payment.successTitle}</h1>
            <p className="max-w-md text-slate-300">{t.payment.successDesc}</p>
            {order && (
              <div className="mt-2 w-full max-w-sm rounded-2xl border border-white/8 bg-vault-card p-5 text-sm">
                <div className="flex justify-between py-1 text-slate-300"><span>{t.payment.orderNumber}</span><span className="font-mono font-semibold text-white" data-testid="success-order-number">{order.order_number}</span></div>
                <div className="flex justify-between py-1 text-slate-300"><span>{t.payment.total}</span><span className="font-mono font-semibold text-white">{formatEur(order.total_eur)}</span></div>
              </div>
            )}
            <Link to="/" data-testid="payment-success-continue" className="btn-glow mt-2 rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-6 py-2.5 text-sm font-semibold text-white">
              {t.payment.continueShopping}
            </Link>
          </div>
        )}
        {state === "failed" && (
          <div data-testid="payment-failed" className="flex flex-col items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-rose-500/15">
              <XCircle className="h-9 w-9 text-rose-400" />
            </div>
            <h1 className="font-display text-2xl font-bold text-white">{t.payment.failedTitle}</h1>
            <p className="max-w-md text-slate-300">{t.payment.failedDesc}</p>
            <Link to="/pokladna" className="btn-glow mt-2 rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-6 py-2.5 text-sm font-semibold text-white">
              {t.payment.backToCart}
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
